import type { NameMismatchAck } from '../types';

/**
 * Flow A name-mismatch guard entry — set when the most recent extraction on an obligation
 * came back with businessNameMatch === false (fail-open — undefined/true never sets one).
 * `acknowledged` flips true once the staffer confirms the override dialog, which un-gates
 * that obligation's Mark-as-Filed button without (on its own) clearing the warning banner.
 *
 * `ack` is the NameMismatchAck built at the moment of that confirmation, persisted on the
 * entry itself (mirrors Flow C's separate `acknowledgedAck` state, just co-located here
 * since Flow A's guard is already a Map keyed by obligationId). It MUST be preserved — not
 * deleted — when the override is confirmed, so a Mark-Filed retry after a transient
 * mark.mutate failure can still resolve and resend the same ack (see resolveMarkFiledAck).
 */
export interface NameGuardEntry {
  extractedName?: string | null;
  score?: number | null;
  matchedAgainst?: 'legal' | 'dba' | null;
  acknowledged: boolean;
  ack?: NameMismatchAck | null;
}

/** Per-obligation guard state: obligationId → its most recent unresolved mismatch (if any). */
export type NameGuardMap = Map<string, NameGuardEntry>;

/**
 * Flow A Mark-Filed gate, factored out as a pure function so it can be unit-tested without
 * mounting the Fluent v9 confirm Dialog (which hangs in jsdom).
 *
 * Returns true when `obligationId` has an unacknowledged name-mismatch entry and the
 * Mark-Filed click should be intercepted to open the confirm-to-override dialog instead of
 * filing directly. Every other obligation's entry (or absence of one) is irrelevant — this
 * is what keeps the gate scoped to the row actually being filed.
 */
export function shouldGateMarkFiled(guards: NameGuardMap, obligationId: string): boolean {
  const entry = guards.get(obligationId);
  return !!entry && !entry.acknowledged;
}

/**
 * Flow A Mark-Filed ack resolution — mirrors resolveManualFilingSaveAck (Flow C) for the
 * per-obligation Map shape. Decides what `ack` argument submitMarkFiled should receive for
 * a Mark-Filed click that is NOT going through the confirm-to-override dialog (i.e.
 * `shouldGateMarkFiled` already returned false for this obligationId). There are two ways
 * to reach that state:
 *  - no guard entry for this obligation (no mismatch, or a later successful extraction
 *    cleared it) → no ack.
 *  - the entry is present AND already acknowledged — most commonly a Mark-Filed RETRY after
 *    a failed override submit (mark.mutate's onError fired, the entry stayed acknowledged
 *    from the first attempt). This path must still return the persisted ack — returning
 *    undefined here is exactly the data-loss bug this helper exists to prevent (a retry
 *    silently dropping the override audit trail).
 */
export function resolveMarkFiledAck(guards: NameGuardMap, obligationId: string): NameMismatchAck | undefined {
  const entry = guards.get(obligationId);
  return entry?.acknowledged ? (entry.ack ?? undefined) : undefined;
}

/**
 * Flow C (RecordManualFilingDialog) name-mismatch guard — a manual filing dialog handles
 * exactly one filing per open session (unlike Flow A's per-obligation task rows), so state
 * is a single nullable entry rather than a Map keyed by obligationId.
 */
export interface ManualFilingNameGuard {
  show: boolean;
  extractedName?: string | null;
  score?: number | null;
  matchedAgainst?: 'legal' | 'dba' | null;
  acknowledged: boolean;
}

/**
 * Flow C Save gate, factored out as a pure function for the same reason as
 * shouldGateMarkFiled — it can be unit-tested without mounting the Fluent v9 confirm
 * Dialog (which hangs in jsdom, especially nested inside the already-open record dialog).
 *
 * Returns true when there's an unacknowledged name-mismatch guard and the Save click
 * should be intercepted to open the confirm-to-override dialog instead of saving directly.
 */
export function shouldGateManualFilingSave(guard: ManualFilingNameGuard | null): boolean {
  return !!guard && guard.show && !guard.acknowledged;
}

/**
 * Flow C Save-click ack resolution — decides what `ack` argument `doSubmit` should receive
 * for a Save click that is NOT going through the confirm-to-override dialog (i.e.
 * `shouldGateManualFilingSave` already returned false for this click). There are two ways
 * to reach that state:
 *  - no guard was ever set this session (no mismatch) → no ack.
 *  - the guard is present AND already acknowledged — most commonly a Save RETRY after a
 *    failed override submit (`doSubmit` threw, the dialog stayed open, `acknowledged` is
 *    still true from the first attempt). This path must still return the persisted ack —
 *    returning undefined here is exactly the data-loss bug this helper exists to prevent
 *    (a retry silently dropping the override audit trail).
 *
 * `acknowledgedAck` is the ack object captured at the moment the staffer confirmed the
 * override (kept in state separately from `guard.acknowledged`, which is just a boolean).
 */
export function resolveManualFilingSaveAck(
  guard: ManualFilingNameGuard | null,
  acknowledgedAck: NameMismatchAck | null,
): NameMismatchAck | undefined {
  return guard?.acknowledged ? (acknowledgedAck ?? undefined) : undefined;
}
